import {syntaxTree} from '@codemirror/language'
import {type Extension, RangeSetBuilder} from '@codemirror/state'
import {Decoration, type DecorationSet, EditorView, ViewPlugin, type ViewUpdate} from '@codemirror/view'
import {clsx} from 'clsx'

import activeBlockStyles from './active-block.module.css'
import styles from './code-block.module.css'

/**
 * Check if a node represents a code block
 */
function isCodeBlock(nodeName: string): boolean {
  return nodeName === 'FencedCode' || nodeName === 'CodeBlock'
}

/**
 * Check if a node represents a code fence delimiter
 */
function isCodeFence(nodeName: string): boolean {
  return nodeName === 'CodeMark'
}

/**
 * Builds code block decorations using the syntax tree
 */
function buildCodeBlockDecorations(view: EditorView): DecorationSet {
  const builder = new RangeSetBuilder<Decoration>()
  const tree = syntaxTree(view.state)

  for (const {from, to} of view.visibleRanges) {
    tree.iterate({
      from,
      to,
      enter: node => {
        if (isCodeBlock(node.name)) {
          // Apply decoration to the entire code block
          builder.add(
            node.from,
            node.to,
            Decoration.mark({
              tagName: 'div',
              class: styles.codeBlock,
            }),
          )

          // Look for child nodes to style fences and content separately
          tree.iterate({
            from: node.from,
            to: node.to,
            enter: child => {
              if (isCodeFence(child.name)) {
                // Style the code fence delimiters (```)
                builder.add(
                  child.from,
                  child.to,
                  Decoration.mark({
                    class: clsx(activeBlockStyles.delimiter, styles.codeFence),
                  }),
                )
              } else if (child.name === 'CodeText') {
                // Style the actual code content
                builder.add(
                  child.from,
                  child.to,
                  Decoration.mark({
                    tagName: 'code',
                    class: styles.codeText,
                  }),
                )
              } else if (child.name === 'CodeInfo') {
                // Style the language info (e.g., "javascript" in ```javascript)
                builder.add(
                  child.from,
                  child.to,
                  Decoration.mark({
                    class: clsx(activeBlockStyles.delimiter, styles.codeInfo),
                  }),
                )
              }
            },
          })
        }
      },
    })
  }

  return builder.finish()
}

/**
 * View plugin that provides code block decorations using syntax tree
 */
const codeBlockDecorationPlugin = ViewPlugin.fromClass(
  class {
    decorations: DecorationSet

    constructor(view: EditorView) {
      this.decorations = buildCodeBlockDecorations(view)
    }

    update(update: ViewUpdate) {
      if (update.docChanged || update.viewportChanged) {
        this.decorations = buildCodeBlockDecorations(update.view)
      }
    }
  },
  {
    decorations: v => v.decorations,
  },
)

/**
 * Extension that provides code block styling for GitHub Flavored Markdown using syntax tree
 */
export const codeBlockExtension: Extension = [codeBlockDecorationPlugin]
